import { useMemo } from 'react';
import { useQuery } from 'react-query';
import api, {
  getAllSites,
  getRepairBuckets,
  getOpenRepairBuckets,
} from '../../../lib/api';
import {
  normaliseSessionRow,
  normaliseFinalisedBucketRow,
  normaliseOpenBucketRow,
  isRepairShellSession,
} from '../utils/reportRows';

/**
 * The four queries ReportsView needs (sites, sessions, finalised repair
 * buckets, open repair buckets), merged into one list of normalised rows.
 *
 * Brief 59 Part 5: the grouped-repairs ("hr:") merge is gone — those synthetic
 * rows had no database row of their own and could never carry a report
 * number, so the real bucket rows below replace them outright. Repair-shell
 * sessions (empty `test_sessions` rows persistCompletedRepairSession writes)
 * are dropped via `isRepairShellSession` before normalising.
 */
export function useReportRows() {
  const { data: sites, error: sitesError, isLoading: loadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => getAllSites(),
  });

  const hasSites = Array.isArray(sites) && sites.length > 0;

  const siteIdsKey = useMemo(
    () => (Array.isArray(sites) ? sites.map((s) => s.id).join(',') : ''),
    [sites],
  );

  const {
    data: allSessions = [],
    isLoading: loadingSessions,
    error: sessionsError,
  } = useQuery({
    queryKey: ['reports-sessions-all-sites', siteIdsKey],
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const list = sites || [];
      const settled = await Promise.allSettled(
        list.map((site) =>
          api.get(`/sessions/site/${site.id}`).then((res) => ({ site, rows: res.data || [] })),
        ),
      );
      const merged = [];
      for (const result of settled) {
        if (result.status !== 'fulfilled') continue;
        const { site, rows } = result.value;
        const label = site.display_name ?? site.name?.trim() ?? site.site_code ?? 'Site';
        for (const session of rows) {
          merged.push({ ...session, reportsSiteId: site.id, reportsSiteLabel: label });
        }
      }
      merged.sort((a, b) => {
        const at = new Date(a.end_time || a.start_time || 0).getTime();
        const bt = new Date(b.end_time || b.start_time || 0).getTime();
        return bt - at;
      });
      return merged;
    },
    enabled: hasSites,
  });

  // No pagination UI for buckets (Brief 59, out of scope) — twelve exist today, well under this limit.
  const {
    data: bucketsPayload,
    isLoading: loadingBuckets,
    error: bucketsError,
  } = useQuery({
    queryKey: ['repair-buckets-for-reports'],
    queryFn: () => getRepairBuckets({ page: 1, limit: 100 }),
    enabled: hasSites,
  });

  const {
    data: openBucketsPayload,
    isLoading: loadingOpenBuckets,
    error: openBucketsError,
  } = useQuery({
    queryKey: ['open-repair-buckets-for-reports'],
    queryFn: () => getOpenRepairBuckets(),
    enabled: hasSites,
  });

  const rows = useMemo(() => {
    const finalisedRows = (bucketsPayload?.buckets || []).map(normaliseFinalisedBucketRow);
    const openRows = (openBucketsPayload?.buckets || []).map(normaliseOpenBucketRow);
    const sessionRows = allSessions.filter((s) => !isRepairShellSession(s)).map(normaliseSessionRow);
    return [...finalisedRows, ...openRows, ...sessionRows];
  }, [allSessions, bucketsPayload, openBucketsPayload]);

  return {
    sites,
    sitesError,
    loadingSites,
    rows,
    isLoadingRows: loadingSessions || loadingBuckets || loadingOpenBuckets,
    sessionsError,
    bucketsError,
    openBucketsError,
  };
}
